import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import maplibregl, {
  FitBoundsOptions,
  Listener,
  Map,
  Marker,
} from 'maplibre-gl';
import { Polygon } from 'geojson';
import SwitchMap from '@/components/ui/map/SwitchMap';
import {
  IDisplayOptions,
  IMapProvider,
  IPoint,
  IPoints,
  ITarget,
} from '@/types/map';
import {
  GNSS,
  arePointsEqual,
  getBearingFromGNSS,
  getBounds,
  getDotColor,
  getGroupedBounds,
  getPixelsPerMeterAtLatitude,
} from '@/utils/map';
import * as HmRaster from '@/components/ui/map/layers/HmRaster';
import * as AppleRaster from '@/components/ui/map/layers/AppleRaster';
import { colors } from '@/utils/styles';
import { Coordinate, GeoJSONFeature } from '@/types/geojson';
import { COLOR_NAME_TO_HEX } from '@/types/pmTileMap';
import {
  azimuthMarkerSvg,
  cameraSvg,
  markerSvg,
  redAzimuthMarkerSvg,
  redMarkerSvg,
  cameraPositionSvg,
  greenAzimuthMarkerSvg,
  greenMarkerSvg,
} from './DisplayOnMapSVGs';

interface Props {
  target: ITarget; // Represents a to-be-submitted response.
  actualTarget?: ITarget; // Represents an already-submitted response.
  expectedTarget?: ITarget; // Represents an expected (i.e. benchmark) response.
  onTargetDragEnd?: (lat: number, lng: number, event?: Listener) => void;
  points?: IPoints;
  marks?: IPoints;
  options?: IDisplayOptions;
  hypotheses?: GeoJSONFeature[];
  zone?: Polygon;
}

const FOCUS_ZOOM = 25;

let targetMarker: Marker;
let actualTargetMarker: Marker;
let expectedTargetMarker: Marker;
let map: Map;
let dots: HTMLDivElement[] = [];
let crosses: HTMLDivElement[] = [];

const MapStatus = {
  Idle: 'Idle',
  Loading: 'Loading',
  Loaded: 'Loaded',
} as const;

type MapStatusType = (typeof MapStatus)[keyof typeof MapStatus];

type MapAction = { type: 'LOAD_MAP' } | { type: 'MAP_LOADED' };

type MapState = {
  status: MapStatusType;
};

const initialMapState: MapState = { status: MapStatus.Idle };

const mapReducer = (state: MapState, action: MapAction): MapState => {
  switch (action.type) {
    case 'LOAD_MAP':
      return { status: MapStatus.Loading };
    case 'MAP_LOADED':
      return { status: MapStatus.Loaded };
    default:
      return state;
  }
};

export function DisplayOnMap({
  target,
  actualTarget,
  expectedTarget,
  onTargetDragEnd,
  points = [],
  marks = [],
  options,
  hypotheses = [],
  zone,
}: Props) {
  const mapContainer: any = useRef<HTMLElement>(null);
  const [mapState, dispatch] = useReducer(mapReducer, initialMapState);

  const [currentMapType, setCurrentMapType] = useState<IMapProvider>(
    IMapProvider.Mapbox,
  );
  const [selectedPolygonId, setSelectedPolygonId] = useState<string | null>(
    null,
  );

  const {
    hasColorDots,
    hasAppleMaps,
    hideTargetMarker,
    alignViewBasedOnPoints,
    selectedCameraIndex,
    initialMapProvider,
  } = options || {};
  const { lat, lon, azimuth } = target || {};
  const prevPointsRef = useRef<IPoints>([]);
  const prevMarksRef = useRef<IPoints>([]);

  const createOrUpdateDots = (
    map: Map,
    points: IPoints,
    hasColorDots?: boolean,
  ) => {
    prevPointsRef.current = points;
    // Clear existing dots if there are any (when receiving new points)
    dots.forEach(dot => {
      if (dot.parentNode) {
        dot.parentNode.removeChild(dot);
      }
    });

    dots = [];
    dots = points.map((point: IPoint, index: number) => {
      let defaultSvg;
      if (point.image) {
        defaultSvg = `<img width="22" height="22" src=${point.image} alt="position correction dot ${index}" />`;
      } else {
        defaultSvg = `<svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="6" cy="6" r="4.69" fill=${
          hasColorDots ? getDotColor(index) : colors.neutral[0]
        } stroke="black" stroke-width="1.7"/>
      </svg>`;
      }

      const dotEl = document.createElement('div');
      dotEl.style.display = 'flex';
      dotEl.innerHTML = point.yaw ? cameraPositionSvg : defaultSvg;

      const dotMarker = new maplibregl.Marker({
        element: dotEl,
        draggable: false,
      });
      dotMarker.setLngLat([point.lon, point.lat]);
      if (point.yaw) {
        dotMarker.setRotation(point.yaw - map.getBearing());
      }
      dotMarker.addTo(map);

      if (selectedCameraIndex === index) {
        dotEl.innerHTML = cameraSvg;
      }

      return dotEl;
    });
  };

  const createOrUpdateCrosses = (map: Map, marks: IPoints) => {
    prevMarksRef.current = marks;
    // Clear existing crosses if there are any (when receiving new marks)
    crosses.forEach(cross => {
      if (cross.parentNode) {
        cross.parentNode.removeChild(cross);
      }
    });

    crosses = [];
    crosses = marks.map((mark: IPoint, index: number) => {
      let svg;
      if (mark.image) {
        svg = `<img width="22" height="22" src=${mark.image} alt="position correction dot ${index}" />`;
      } else {
        svg = `<svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
          <line x1="2" y1="2" x2="10" y2="10" stroke="${getDotColor(index)}" stroke-width="1.7"/>
          <line x1="10" y1="2" x2="2" y2="10" stroke="${getDotColor(index)}" stroke-width="1.7"/>
        </svg>`;
      }

      const crossEl = document.createElement('div');
      crossEl.style.display = 'flex';
      crossEl.innerHTML = svg;

      const crossMarker = new maplibregl.Marker({
        element: crossEl,
        draggable: false,
      });
      crossMarker.setLngLat([mark.lon, mark.lat]).addTo(map);

      return crossEl;
    });
  };

  function calculatePolygonCenter(coordinates: GeoJSON.Position[]) {
    let x = 0,
      y = 0;
    coordinates.forEach(coord => {
      x += coord[0];
      y += coord[1];
    });

    return [x / coordinates.length, y / coordinates.length];
  }

  function addNearTargetProperty(
    geojson: GeoJSON.FeatureCollection,
    tolerance = 0.00005,
  ) {
    geojson.features.forEach(feature => {
      const center = calculatePolygonCenter(
        (feature.geometry as GeoJSON.Polygon).coordinates[0],
      );
      const isNearTarget =
        Math.abs(center[0] - lon) < tolerance &&
        Math.abs(center[1] - lat) < tolerance;

      if (!feature.properties) {
        feature.properties = {};
      }
      feature.properties.nearTarget = isNearTarget;
    });
    return geojson;
  }

  const addHypothesesLayers = (map: Map, hypotheses: GeoJSONFeature[]) => {
    if (!map) return;

    if (hypotheses.length > 0) {
      const featureCollection: GeoJSON.FeatureCollection = {
        type: 'FeatureCollection',
        features: hypotheses as unknown as GeoJSON.Feature[],
      };

      addNearTargetProperty(featureCollection);

      map.addSource('hypotheses', {
        type: 'geojson',
        data: featureCollection,
      });

      map.addLayer({
        id: 'hypotheses-layer',
        type: 'fill',
        source: 'hypotheses',
        layout: {},
        paint: {
          'fill-opacity': 0.5,
          'fill-color': [
            'case',
            ['==', ['get', 'nearTarget'], true],
            COLOR_NAME_TO_HEX['green'], // Green if landmark pos is already on target
            ['==', ['get', 'id'], selectedPolygonId ?? ''],
            COLOR_NAME_TO_HEX['green'], // Green if selected
            COLOR_NAME_TO_HEX['red'], // Red otherwise
          ],
        },
      });

      map.on('click', 'hypotheses-layer', e => {
        // Set position to center of clicked hypothesis
        const clickedFeature = e.features ? e.features[0] : undefined;

        if (clickedFeature) {
          const coordinates = (clickedFeature.geometry as GeoJSON.Polygon)
            .coordinates[0];
          const polygonId = clickedFeature.properties.id;
          setSelectedPolygonId(polygonId);

          map.setPaintProperty('hypotheses-layer', 'fill-color', [
            'case',
            ['==', ['get', 'id'], polygonId],
            COLOR_NAME_TO_HEX['green'], // Selected color
            COLOR_NAME_TO_HEX['red'], // Default color
          ]);

          const [centerX, centerY] = calculatePolygonCenter(coordinates);

          // Do something with the centroid, e.g., log it or trigger an action
          onTargetDragEnd?.(centerY, centerX);
        }
      });
    }
  };

  const addZoneLayer = (map: Map, zone: Polygon) => {
    if (!map || !zone) return;

    if (!map.getSource('zone')) {
      map.addSource('zone', {
        type: 'geojson',
        data: zone,
      });
    }

    map.addLayer({
      id: 'zone-layer',
      type: 'fill',
      source: 'zone',
      layout: {},
      paint: {
        'fill-opacity': 0.5,
        'fill-color': COLOR_NAME_TO_HEX['blue'],
      },
    });
  };

  function scaleSVGWrapper(
    svgWrapper: HTMLElement,
    pixelsPerMeter: number,
    scalingFactor: number,
    azimuth: number | undefined,
  ) {
    if (azimuth != null) {
      svgWrapper.style.width = `${9 * pixelsPerMeter * scalingFactor}px`;
      svgWrapper.style.height = `${9 * pixelsPerMeter * scalingFactor}px`;
    } else {
      svgWrapper.style.width = `${6 * pixelsPerMeter * scalingFactor}px`;
      svgWrapper.style.height = `${6 * pixelsPerMeter * scalingFactor}px`;
    }
  }

  const updateZoom = useCallback(() => {
    if (!map) return;
    const zoom = map.getZoom();
    const pixelsPerMeter = getPixelsPerMeterAtLatitude(lat, zoom);
    const base = 1.6;
    const maxZoom = 20;
    const scalingFactor = Math.pow(base, maxZoom - zoom);

    const svgWrapper = targetMarker.getElement();
    scaleSVGWrapper(svgWrapper, pixelsPerMeter, scalingFactor, azimuth);
    const actualSvgWrapper = actualTargetMarker?.getElement();
    if (actualSvgWrapper) {
      scaleSVGWrapper(actualSvgWrapper, pixelsPerMeter, scalingFactor, azimuth);
    }
    const expectedSvgWrapper = expectedTargetMarker?.getElement();
    if (expectedSvgWrapper) {
      scaleSVGWrapper(
        expectedSvgWrapper,
        pixelsPerMeter,
        scalingFactor,
        azimuth,
      );
    }
  }, [azimuth, lat]);

  const handleMapTypeChange = useCallback((newMapType: IMapProvider | null) => {
    if (!map || newMapType === null) {
      return;
    }

    setCurrentMapType(newMapType);

    if (newMapType === IMapProvider.Apple) {
      AppleRaster.addLayer(map);
      HmRaster.removeLayer(map);
    } else {
      HmRaster.addLayer(map);
      AppleRaster.removeLayer(map);
    }
  }, []);

  useEffect(() => {
    if (
      mapState.status !== MapStatus.Loaded ||
      (arePointsEqual(prevPointsRef.current, points) &&
        arePointsEqual(prevMarksRef.current, marks))
    ) {
      return;
    } else {
      createOrUpdateDots(map, points, hasColorDots);
      createOrUpdateCrosses(map, marks);
      addHypothesesLayers(map, hypotheses);
      if (zone) addZoneLayer(map, zone);
      const pointsAndMarks = points.concat(marks);
      // lets update the view based on these new points/marks
      const bounds = getGroupedBounds({ lat, lon }, pointsAndMarks, 0);
      map.fitBounds(bounds, { padding: 100, duration: 0 });
    }
  }, [points, hasColorDots, marks, lat, lon, mapState.status, zone]);

  const handleDragEnd = useCallback(
    (event: Listener) => {
      const { lat, lng } = targetMarker.getLngLat();
      if (onTargetDragEnd) {
        onTargetDragEnd(lat, lng, event);
      }
    },
    [onTargetDragEnd],
  );

  const handleMapLoad = useCallback(() => {
    let mapBearing;
    const options: FitBoundsOptions = { padding: 100, duration: 0 };
    createOrUpdateDots(map, points, hasColorDots);
    createOrUpdateCrosses(map, marks);
    addHypothesesLayers(map, hypotheses);
    if (zone) addZoneLayer(map, zone);
    const markerEl = document.createElement('div');
    markerEl.style.display = 'flex';
    const actualMarkerEl = document.createElement('div');
    actualMarkerEl.style.display = 'flex';
    const expectedMarkerEl = document.createElement('div');
    expectedMarkerEl.style.display = 'flex';

    targetMarker = new maplibregl.Marker({
      element: markerEl,
      draggable: false,
    });
    targetMarker.setLngLat([lon, lat]).addTo(map);

    if (actualTarget) {
      actualTargetMarker = new maplibregl.Marker({
        element: actualMarkerEl,
        draggable: false,
      });
      actualTargetMarker
        .setLngLat([actualTarget.lon, actualTarget.lat])
        .addTo(map);
    }
    if (expectedTarget) {
      expectedTargetMarker = new maplibregl.Marker({
        element: expectedMarkerEl,
        draggable: false,
      });
      expectedTargetMarker
        .setLngLat([expectedTarget.lon, expectedTarget.lat])
        .addTo(map);
    }

    if (onTargetDragEnd) {
      targetMarker.setDraggable(true);
      targetMarker.on('dragend', handleDragEnd);
    }

    if (alignViewBasedOnPoints) {
      const gnssPoints: GNSS[] = points.map((point: IPoint) => ({
        lat: point.lat,
        lon: point.lon,
        t: 0,
      }));

      mapBearing = getBearingFromGNSS(gnssPoints);
      if (mapBearing) {
        options.bearing = mapBearing;
      }
    }

    if (!hideTargetMarker) {
      if (azimuth != null) {
        markerEl.innerHTML = azimuthMarkerSvg;
      } else {
        markerEl.innerHTML = markerSvg;
      }
      if (actualTarget) {
        if (actualTarget.azimuth != null) {
          actualMarkerEl.innerHTML = redAzimuthMarkerSvg;
        } else {
          actualMarkerEl.innerHTML = redMarkerSvg;
        }
      }
      if (expectedTarget) {
        if (expectedTarget.azimuth != null) {
          expectedMarkerEl.innerHTML = greenAzimuthMarkerSvg;
        } else {
          expectedMarkerEl.innerHTML = greenMarkerSvg;
        }
      }
      updateZoom();
    }
    map.on('zoom', updateZoom);

    if (hypotheses.length > 0) {
      const boundCoords = hypotheses.map(h =>
        getBounds(h.geometry.coordinates[0] as Coordinate[]),
      );
      const coords = (
        boundCoords as [
          { lon: number; lat: number },
          { lon: number; lat: number },
        ][]
      )
        .map(b => [
          [b[0].lon, b[0].lat],
          [b[1].lon, b[1].lat],
        ])
        .flat();
      coords.push(...points.map(p => [p.lon, p.lat]));
      const bounds = getBounds(coords as [number, number][]);
      map.fitBounds(bounds, { ...options, padding: 10 });
    } else {
      const bounds = getGroupedBounds({ lat, lon }, points, 0);
      map.fitBounds(bounds, options);
    }
    if (hasAppleMaps) {
      const initApple = async () => {
        try {
          await AppleRaster.init();
          AppleRaster.addSource(map);
          if (initialMapProvider === IMapProvider.Apple) {
            handleMapTypeChange(IMapProvider.Apple);
          }
        } catch (error) {
          console.error('Failed to initialize Apple Maps', error);
        } finally {
          dispatch({ type: 'MAP_LOADED' });
        }
      };

      initApple();
    } else {
      dispatch({ type: 'MAP_LOADED' });
    }
  }, [
    points,
    hasColorDots,
    marks,
    lon,
    lat,
    actualTarget,
    expectedTarget,
    onTargetDragEnd,
    alignViewBasedOnPoints,
    hideTargetMarker,
    updateZoom,
    hasAppleMaps,
    handleDragEnd,
    azimuth,
    initialMapProvider,
    handleMapTypeChange,
  ]);

  useEffect(() => {
    if (!mapContainer.current || mapState.status !== MapStatus.Idle) {
      return;
    }

    dispatch({ type: 'LOAD_MAP' });

    map = HmRaster.getMapLibreBaseMap({
      lat,
      lon,
      mapContainerCurrent: mapContainer.current,
      points,
    });

    map.on('load', handleMapLoad);

    return () => {
      // Only clean up if the map is fully initialized
      if (map && mapState.status === MapStatus.Loaded) {
        map.off('load', handleMapLoad);
        map.off('zoom', updateZoom);
        if (onTargetDragEnd && targetMarker) {
          targetMarker.off('dragend', handleDragEnd);
        }
      }
    };
  }, [
    lat,
    lon,
    updateZoom,
    onTargetDragEnd,
    points,
    mapState.status,
    handleDragEnd,
    handleMapLoad,
  ]);

  useEffect(() => {
    return () => {
      map.remove();
    };
  }, []);

  useEffect(() => {
    const updateRotation = () => {
      let currentRotation = map.getBearing();

      if (currentRotation < 180) {
        currentRotation *= -1;
      } else {
        currentRotation = 360 - currentRotation;
      }

      if (targetMarker && azimuth) {
        targetMarker.setRotation(azimuth - map.getBearing());
      }
      if (actualTargetMarker && actualTarget && actualTarget.azimuth) {
        actualTargetMarker.setRotation(actualTarget.azimuth - map.getBearing());
      }
      if (expectedTargetMarker && expectedTarget && expectedTarget.azimuth) {
        expectedTargetMarker.setRotation(
          expectedTarget.azimuth - map.getBearing(),
        );
      }

      updateDots();
    };

    if (
      mapState.status === MapStatus.Loaded &&
      (azimuth != null || points.some(point => point.yaw))
    ) {
      map.on('rotate', updateRotation);
      updateRotation();
    }

    return () => {
      if (map) {
        map.off('rotate', updateRotation);
      }
    };
  }, [actualTarget, azimuth, expectedTarget, mapState.status]);

  const updateDots = useCallback(() => {
    if (!map) return;
    createOrUpdateDots(map, points, hasColorDots);
    const dotSvg = `<svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="6" cy="6" r="4.69" fill=${colors.neutral[0]} stroke="black" stroke-width="1.7"/>
    </svg>`;
    if (selectedCameraIndex !== undefined) {
      dots = dots.map((dot: any, index: number) => {
        if (dot.parentNode) {
          dot.parentNode.removeChild(dot);
        }

        const dotEl = document.createElement('div');
        dotEl.style.display = 'flex';

        const yaw = points[index] ? points[index].yaw : undefined;
        if (index === selectedCameraIndex) {
          dotEl.innerHTML = cameraSvg;
        } else if (yaw) {
          dotEl.innerHTML = cameraPositionSvg;
        } else {
          dotEl.innerHTML = dotSvg;
        }

        const dotMarker = new maplibregl.Marker({
          element: dotEl,
          draggable: false,
        });
        dotMarker.setLngLat([points[index].lon, points[index].lat]);
        if (selectedCameraIndex !== index && yaw) {
          dotMarker.setRotation(yaw - map.getBearing());
        }
        dotMarker.addTo(map);

        return dotEl;
      });
    }
  }, [selectedCameraIndex]);

  useEffect(() => {
    if (
      selectedCameraIndex !== undefined &&
      mapState.status === MapStatus.Loaded
    ) {
      updateDots();
    }
  }, [selectedCameraIndex, mapState.status, updateDots]);

  useEffect(() => {
    const handleKeyDown = (event: { key: string }) => {
      if (event.key === 'f' || event.key === 'F') {
        if (targetMarker) {
          map.flyTo({
            center: [lon, lat],
            zoom: FOCUS_ZOOM,
            speed: 1.2,
          });
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  });

  return (
    <>
      <div
        ref={mapContainer}
        className="relative size-full transition duration-500"
        style={{ opacity: mapState.status === MapStatus.Loaded ? 1 : 0 }}
      />
      {hasAppleMaps && (
        <SwitchMap
          handleMapChange={mapType => handleMapTypeChange(mapType)}
          currentMap={currentMapType}
        />
      )}
    </>
  );
}
